import React, { useState, useEffect, useRef } from 'react';
import {
    Menu,
    Box,
    Typography,
    MenuItem,
    Divider,
    Button,
    Avatar,
    Chip,
    FormControl,
    Select,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    List,
    ListItem,
    Grid
} from '@mui/material';
import { GoogleMap, useJsApiLoader, MarkerF } from '@react-google-maps/api';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// 🎨 Importamos tu interceptor de axios y estilos de mapa
import api from "../../assets/utils/axiosConfig";
import mapStyles from "../../assets/estilos/mapStyles";

const NotificationMenu = ({ anchorEl, onClose, setUnreadCount }) => {
    const open = Boolean(anchorEl);
    
    const [notificaciones, setNotificaciones] = useState([]);
    const [totalesSinLeer, setTotalesSinLeer] = useState(0);
    const [filtro, setFiltro] = useState('todos');
    const [cargando, setCargando] = useState(false);

    // Estado Modal: Detalle Individual
    const [openModalDetalle, setOpenModalDetalle] = useState(false);
    const [notifSeleccionada, setNotifSeleccionada] = useState(null);

    // Estado Modal: Ver Todo el Historial
    const [openModalVerTodo, setOpenModalVerTodo] = useState(false);
    const [todasNotificaciones, setTodasNotificaciones] = useState([]);
    const [cargandoHistorial, setCargandoHistorial] = useState(false);

    // 🗺️ Google Maps API Loader
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || ""
    });

    useEffect(() => {
        cargarNotificaciones();
    }, []);

    useEffect(() => {
        if (open) {
            cargarNotificaciones();
        }
    }, [open, filtro]);

    const cargarNotificaciones = async () => {
        setCargando(true);
        try {
            const response = await api.post("Notificaciones/ObtenerNotificaciones.php", { 
                origen: filtro, 
                limite: 5, 
                offset: 0 
            });

            const data = response.data;
            setNotificaciones(data.notificaciones || []);
            setTotalesSinLeer(data.totales_sin_leer || 0);
            if (setUnreadCount) setUnreadCount(data.totales_sin_leer || 0);
        } catch (error) {
            console.error("Error al cargar notificaciones:", error.response?.data || error.message);
        } finally {
            setCargando(false);
        }
    };

    const cargarHistorialCompleto = async () => {
        setCargandoHistorial(true);
        try {
            const response = await api.post("Notificaciones/ObtenerNotificaciones.php", { 
                origen: 'todos', 
                limite: 50, 
                offset: 0 
            });

            const data = response.data;
            setTodasNotificaciones(data.notificaciones || []);
        } catch (error) {
            console.error("Error cargando historial:", error.response?.data || error.message);
        } finally {
            setCargandoHistorial(false);
        }
    };

    const handleSelectNotificacion = async (notif) => {
        setNotifSeleccionada(notif);
        setOpenModalDetalle(true);
        onClose(); 

        if (notif.leido) return;

        try {
            await api.post("Notificaciones/MarcarLeida.php", { 
                id: notif.id, 
                origen: notif.origen 
            });

            setNotificaciones(prev => prev.map(item => item.id === notif.id && item.origen === notif.origen ? { ...item, leido: true } : item));
            setTodasNotificaciones(prev => prev.map(item => item.id === notif.id && item.origen === notif.origen ? { ...item, leido: true } : item));
            
            setTotalesSinLeer(prev => {
                const nuevoTotal = Math.max(0, prev - 1);
                if (setUnreadCount) setUnreadCount(nuevoTotal);
                return nuevoTotal;
            });
        } catch (error) {
            console.error("Error al marcar como leída:", error.response?.data || error.message);
        }
    };

    const handleMarcarTodasComoLeidas = async () => {
        try {
            const response = await api.post("Notificaciones/MarcarTodasLeidas.php");

            if (response.status === 200) {
                setNotificaciones(prev => prev.map(item => ({ ...item, leido: true })));
                setTodasNotificaciones(prev => prev.map(item => ({ ...item, leido: true })));
                setTotalesSinLeer(0);
                if (setUnreadCount) setUnreadCount(0);
            }
        } catch (error) {
            console.error("Error al marcar todas como leídas:", error.response?.data || error.message);
        }
    };

    const handleAbrirVerTodo = () => {
        onClose();
        setOpenModalVerTodo(true);
        cargarHistorialCompleto();
    };

    const formatearFecha = (fechaString) => {
        if (!fechaString) return "";
        const fecha = new Date(fechaString.replace(/-/g, '/'));
        return fecha.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    };

    const obtenerIconoPersonalizado = (tipo) => {
        let color = '#64748B'; 
        if (tipo === 'ignicion') color = '#22C55E';  
        if (tipo === 'velocidad') color = '#EF4444';  
        if (tipo === 'geocerca') color = '#3B82F6';   

        const svg = `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40">
                <path fill="${color}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                <circle cx="12" cy="9" r="2.5" fill="#FFFFFF"/>
            </svg>
        `;
        return {
            url: `data:image/svg+xml;utf-8,${encodeURIComponent(svg)}`,
            scaledSize: typeof window !== 'undefined' && window.google ? new window.google.maps.Size(40, 40) : null
        };
    };

    return (
        <>
            <Menu
                anchorEl={anchorEl} open={open} onClose={onClose}
                PaperProps={{
                    elevation: 4,
                    sx: { width: 360, borderRadius: 3, p: 2, mt: 1.5, border: "1px solid #E2E8F0" }
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight="700" color="#0F172A">Notificaciones</Typography>
                    {totalesSinLeer > 0 && (
                        <Chip label={totalesSinLeer} size="small" sx={{ bgcolor: "#EF4444", color: "#fff", fontWeight: "bold" }} />
                    )}
                </Box>

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <Select value={filtro} onChange={(e) => setFiltro(e.target.value)} sx={{ borderRadius: "8px", fontSize: "14px" }}>
                        <MenuItem value="todos">Todas las notificaciones</MenuItem>
                        <MenuItem value="vehiculos">Alertas de Vehículos</MenuItem>
                        <MenuItem value="sistema">Avisos del Sistema</MenuItem>
                    </Select>
                </FormControl>

                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                    {cargando ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={20} /></Box>
                    ) : notificaciones.map((notif) => (
                        <MenuItem 
                            key={`${notif.origen}-${notif.id}`} onClick={() => handleSelectNotificacion(notif)}
                            sx={{ flexDirection: 'column', alignItems: 'start', py: 1, px: 1, borderRadius: "8px", mb: 0.5, whiteSpace: 'normal', backgroundColor: !notif.leido ? "#F8FAFC" : "transparent" }}
                        >
                            <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: notif.avatar_color, fontSize: "11px", fontWeight: "bold" }}>{notif.avatar_letras}</Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography fontWeight="600" fontSize={13} color="#1E293B">{notif.titulo}</Typography>
                                        <Typography fontSize={10} color="#94A3B8">{formatearFecha(notif.fecha)}</Typography>
                                    </Box>
                                    <Typography variant="caption" color="#475569">{notif.mensaje}</Typography>
                                </Box>
                            </Box>
                        </MenuItem>
                    ))}
                </Box>
                <Divider sx={{ my: 1 }} />
                <Button fullWidth onClick={handleAbrirVerTodo} sx={{ color: "#2FA4B8", fontWeight: "600", textTransform: "none" }}>
                    Ver todas las notificaciones
                </Button>
            </Menu>

            <Dialog open={openModalDetalle} onClose={() => setOpenModalDetalle(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 36, height: 36, fontSize: 14, bgcolor: notifSeleccionada?.avatar_color }}>{notifSeleccionada?.avatar_letras}</Avatar>
                        <Typography variant="h6" fontWeight="700" fontSize={17} color="#0F172A">{notifSeleccionada?.titulo}</Typography>
                    </Box>
                    <IconButton size="medium" onClick={() => setOpenModalDetalle(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                
                <DialogContent dividers sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, bgcolor: '#F8FAFC' }}>
                    <Box sx={{ bgcolor: '#FFF1F2', color: '#991B1B', p: 1.8, borderRadius: '8px', border: '1px solid #FEE2E2', fontSize: '14px', fontWeight: '500', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                        {notifSeleccionada?.mensaje}
                    </Box>

                    {notifSeleccionada?.origen === 'vehiculo' && notifSeleccionada?.vehiculo && (
                        <Grid container spacing={2} alignItems="center" sx={{ px: 0.5 }}>
                            <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                                {notifSeleccionada.vehiculo.foto ? (
                                    <Box 
                                        component="img" 
                                        src={notifSeleccionada.vehiculo.foto} 
                                        alt="Vehículo" 
                                        sx={{ width: '100%', height: 75, objectFit: 'cover', borderRadius: '8px', border: '1px solid #E2E8F0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }} 
                                    />
                                ) : (
                                    <Avatar variant="rounded" sx={{ width: 70, height: 70, bgcolor: '#E2E8F0', color: '#475569' }}>
                                        <DirectionsCarIcon fontSize="large" />
                                    </Avatar>
                                )}
                            </Grid>

                            <Grid item xs={9}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography variant="body1" fontWeight="700" sx={{ bgcolor: '#FEF08A', color: '#854D0E', px: 1, py: 0.3, borderRadius: '4px', fontSize: '13px', fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                                        {notifSeleccionada.vehiculo.patente}
                                    </Typography>
                                    <Typography variant="subtitle1" fontWeight="600" color="#1E293B">
                                        {notifSeleccionada.vehiculo.marca} {notifSeleccionada.vehiculo.modelo}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: '#475569' }}>
                                    <AccessTimeIcon sx={{ color: '#64748B', fontSize: 16 }} />
                                    <Typography variant="body2" fontWeight="500">
                                        Reportado el {formatearFecha(notifSeleccionada.fecha)} hrs.
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    )}

                    {notifSeleccionada?.origen === 'vehiculo' && notifSeleccionada?.lat && notifSeleccionada?.lon && isLoaded ? (
                        <Box sx={{ width: '100%', height: 260, borderRadius: '12px', overflow: 'hidden', border: '1px solid #E2E8F0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                            <GoogleMap
                                mapContainerStyle={{ width: "100%", height: "100%" }}
                                zoom={15}
                                center={{ lat: Number(notifSeleccionada.lat), lng: Number(notifSeleccionada.lon) }}
                                options={{
                                    streetViewControl: false,
                                    fullscreenControl: false,
                                    mapTypeControl: false,
                                    styles: mapStyles,
                                    zoomControl: true,
                                }}
                            >
                                <MarkerF 
                                    position={{ lat: Number(notifSeleccionada.lat), lng: Number(notifSeleccionada.lon) }}
                                    icon={obtenerIconoPersonalizado(notifSeleccionada.tipo)}
                                />
                            </GoogleMap>
                        </Box>
                    ) : (
                        notifSeleccionada?.origen === 'vehiculo' && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenModalDetalle(false)} variant="contained" sx={{ bgcolor: '#475569', '&:hover': { bgcolor: '#334155' }, textTransform: 'none', borderRadius: '8px', px: 3 }}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openModalVerTodo} onClose={() => setOpenModalVerTodo(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#F8FAFC' }}>
                    <Box>
                        <Typography variant="h5" fontWeight="700" color="#0F172A">Historial de Notificaciones</Typography>
                        <Typography variant="caption" color="#64748B">Listado completo de alertas y auditorías del sistema</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {totalesSinLeer > 0 && (
                            <Button 
                                variant="outlined" color="success" size="small" startIcon={<DoneAllIcon />} onClick={handleMarcarTodasComoLeidas}
                                sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: '600' }}
                            >
                                Marcar todas como leídas
                            </Button>
                        )}
                        <IconButton onClick={() => setOpenModalVerTodo(false)}><CloseIcon /></IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers sx={{ p: 0, maxHeight: '60vh', bgcolor: '#F1F5F9' }}>
                    {cargandoHistorial ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}><CircularProgress /></Box>
                    ) : todasNotificaciones.length === 0 ? (
                        <Box sx={{ py: 8, textAlign: 'center' }}><Typography color="#64748B">No hay registros históricos.</Typography></Box>
                    ) : (
                        <List sx={{ p: 1 }}>
                            {todasNotificaciones.map((notif) => (
                                <ListItem 
                                    key={`all-${notif.origen}-${notif.id}`} disablePadding
                                    sx={{ 
                                        bgcolor: 'background.paper', mb: 1, borderRadius: '10px', border: '1px solid #E2E8F0', overflow: 'hidden',
                                        transition: '0.2s', '&:hover': { boxShadow: '0px 4px 12px rgba(0,0,0,0.05)' },
                                        borderLeft: !notif.leido ? '4px solid #2FA4B8' : '4px solid transparent'
                                    }}
                                >
                                    <Box onClick={() => handleSelectNotificacion(notif)} sx={{ display: 'flex', width: '100%', p: 2, cursor: 'pointer', alignItems: 'center', gap: 2 }}>
                                        <Avatar sx={{ bgcolor: notif.avatar_color, width: 42, height: 42 }}>{notif.avatar_letras}</Avatar>
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography fontWeight="700" fontSize={14.5} color="#1E293B">{notif.titulo}</Typography>
                                                <Typography fontSize={11} color="#94A3B8">{formatearFecha(notif.fecha)}</Typography>
                                            </Box>
                                            <Typography variant="body2" color="#475569" sx={{ pr: 2 }}>{notif.mensaje}</Typography>
                                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                                <Chip size="small" label={notif.origen === 'vehiculo' ? "Módulo GPS" : "Sistema"} sx={{ height: 20, fontSize: 10 }} />
                                                {notif.origen === 'vehiculo' && notif.lat && (
                                                    <Chip size="small" label="Posee Ubicación" color="info" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#F8FAFC' }}>
                    <Button onClick={() => setOpenModalVerTodo(false)} variant="contained" sx={{ bgcolor: '#475569', textTransform: 'none', borderRadius: '8px' }}>
                        Cerrar Ventana
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default NotificationMenu;